/**
 * Renderless component: flushes queued push-open beacons the moment
 * a valid auth session resolves. Handles the iOS cold-launch case
 * where the Capacitor `pushNotificationActionPerformed` listener
 * fires (via `reportPushOpen`) before the better-auth session
 * cookie is validated, and the initial POST returns 401.
 *
 * Runs once per userId transition (null -> user) via a ref guard.
 * Small enough that a lambda-flake on the flush call just leaves
 * the queue populated for the next launch, no user-visible failure.
 */
import { useEffect, useRef } from "react";
import { flushPushOpenQueue } from "../lib/push";

type Props = { userId: string | null };

export function PushOpenQueueFlusher({ userId }: Props) {
  const lastFlushedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!userId) return;
    if (lastFlushedRef.current === userId) return;
    lastFlushedRef.current = userId;
    void flushPushOpenQueue();
  }, [userId]);
  return null;
}
